/**
 * domainAwardResolver — single entry point for crediting a currency from a
 * domain event.
 *
 * The award rule (in priority order):
 *   1. The event's per-type config wins: `type.award = { currencyId, amount }`.
 *   2. Otherwise the domain's declared currency: `domain.currencyId`, with
 *      amount taken from the event's `points`/`amount` field, falling back to 1.
 *   3. Built-in recognition and discipline events fall back to the school's
 *      sole active house currency. Recognition adds; discipline subtracts.
 *   4. If none of those rules resolves unambiguously, no transaction is
 *      recorded.
 *
 * Caches domain + currency lookups in-process; per-event-type docs are read
 * fresh because their `award` field is the part most likely to change at
 * runtime (admins editing point values).
 */

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { recordTransaction } from './currencyLedger';
import { getCurrency, getCurrenciesByGroup } from './currencyService';

const _domainCache = new Map();
const _currencyCache = new Map();
const _houseCurrencyCache = new Map();
const HOUSE_CURRENCY_CACHE_TTL = 60 * 1000;

async function loadDomain(schoolId, domainId) {
  const key = `${schoolId}:${domainId}`;
  if (_domainCache.has(key)) return _domainCache.get(key);
  try {
    const snap = await getDoc(doc(db, 'schools', schoolId, 'domains', domainId));
    const data = snap.exists() ? { id: snap.id, ...snap.data() } : null;
    _domainCache.set(key, data);
    return data;
  } catch (err) {
    console.warn(`[domainAward] failed to load domain ${domainId}:`, err.message);
    return null;
  }
}

async function loadType(schoolId, typesCollection, typeId) {
  if (!schoolId || !typesCollection || !typeId) return null;
  try {
    const snap = await getDoc(doc(db, 'schools', schoolId, typesCollection, typeId));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch (err) {
    console.warn(`[domainAward] failed to load type ${typeId}:`, err.message);
    return null;
  }
}

async function loadCurrency(schoolId, currencyId) {
  const key = `${schoolId}:${currencyId}`;
  if (_currencyCache.has(key)) return _currencyCache.get(key);
  const cfg = await getCurrency(schoolId, currencyId);
  _currencyCache.set(key, cfg);
  return cfg;
}

function resolveAward({ domain, type, event }) {
  if (type?.award?.currencyId && type?.award?.amount) {
    return { currencyId: type.award.currencyId, amount: type.award.amount };
  }
  const currencyId = domain?.currencyId;
  if (!currencyId) return null;
  const amount = Number(event?.points ?? event?.amount ?? 1);
  if (!Number.isFinite(amount) || amount === 0) return null;
  return { currencyId, amount };
}

export function resolveBuiltInHouseAmount(domainId, event = {}) {
  const rawAmount = Number(event.points ?? event.amount ?? 1);
  if (!Number.isFinite(rawAmount) || rawAmount === 0) return null;
  if (domainId === 'discipline') return -Math.abs(rawAmount);
  if (domainId === 'recognition') return Math.abs(rawAmount);
  return null;
}

async function loadSoleHouseCurrency(schoolId) {
  const cached = _houseCurrencyCache.get(schoolId);
  if (cached && Date.now() - cached.timestamp < HOUSE_CURRENCY_CACHE_TTL) {
    return cached.currency;
  }

  const currencies = await getCurrenciesByGroup(schoolId, 'house');
  const currency = currencies.length === 1 ? currencies[0] : null;
  _houseCurrencyCache.set(schoolId, { currency, timestamp: Date.now() });

  if (currencies.length > 1) {
    console.warn(
      `[domainAward] ${schoolId} has multiple active house currencies; ` +
      'built-in event fallback is disabled until one is selected explicitly'
    );
  }

  return currency;
}

/**
 * Record a currency transaction for a domain event, deriving the award from
 * the domain + per-type config. Safe to call fire-and-forget — errors are
 * caught and logged.
 *
 * @param {Object} args
 * @param {string} args.schoolId
 * @param {string} args.domainId             - domain doc id (e.g. "discipline")
 * @param {string} [args.typesCollection]    - collection of per-type docs (e.g. "mentionTypes")
 * @param {string} [args.typeId]             - per-type doc id, if any
 * @param {string} [args.eventId]            - id of the event being credited
 * @param {Object} [args.event]              - event payload (studentId, points, etc.)
 * @param {{ currencyId: string, amount: number }} [args.override] - skip lookup, use this
 * @returns {Promise<string|null>} ledger transaction id, or null if nothing recorded
 */
export async function recordAwardForEvent({
  schoolId,
  domainId,
  typesCollection,
  typeId,
  eventId,
  event = {},
  override = null,
}) {
  if (!schoolId || !domainId) return null;
  if (!event.studentId) return null;

  let award = override;
  if (!award) {
    const domain = await loadDomain(schoolId, domainId);
    const resolvedTypesCollection =
      typesCollection || domain?.collections?.types || null;
    const type = await loadType(schoolId, resolvedTypesCollection, typeId);
    award = resolveAward({ domain, type, event });

    if (!award && (domainId === 'discipline' || domainId === 'recognition')) {
      const fallbackCurrency = await loadSoleHouseCurrency(schoolId);
      const amount = resolveBuiltInHouseAmount(domainId, event);
      if (fallbackCurrency && amount) {
        award = { currencyId: fallbackCurrency.id, amount };
      }
    }
  }
  if (award && domainId === 'discipline') {
    award = { ...award, amount: -Math.abs(Number(award.amount)) };
  }
  if (!award?.currencyId || !award?.amount) return null;

  const currency = await loadCurrency(schoolId, award.currencyId);
  if (!currency) {
    console.warn(`[domainAward] currency ${award.currencyId} not found for ${schoolId}`);
    return null;
  }

  return recordTransaction(schoolId, currency, {
    studentId: event.studentId,
    studentName: event.studentName,
    className: event.className,
    amount: award.amount,
    sourceType: 'domain_event',
    sourceId: eventId,
    domainId,
    typeLabel: event.typeLabel || event.mentionType || event.recognitionType || null,
    issuedById: event.issuedById,
    issuedByName: event.issuedByName,
    occurredAt: event.occurredAt || event.issuedAt || event.createdAt || null,
  });
}

export const __testing = { resolveAward, resolveBuiltInHouseAmount };
